import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { KLASSEN_LABELS } from "./data";
import { SAMMELGRID_COLUMNS } from "./SammelvertragGrid";
import type { Vereinbarung } from "./vereinbarungMock";

const GROUP_LABEL = {
  kh: "KH",
  tk: "TK",
  vktk: "VK/TK",
} as const;
const GROUP_LONG = {
  kh: "Kraftfahrzeug-Haftpflicht",
  tk: "Teilkasko",
  vktk: "Vollkasko und Teilkasko",
} as const;

const useStyles = makeStyles({
  tableScroll: {
    overflowX: "auto",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    backgroundColor: BRAND.white,
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
  },
  thGroup: {
    position: "relative",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: BRAND.white,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    textAlign: "center",
    ...shorthands.borderRight("1px", "solid", "rgba(255,255,255,0.18)"),
  },
  thGroupShort: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "0.78rem",
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  thGroupLong: {
    display: "inline-block",
    fontSize: "0.78rem",
    fontWeight: 600,
  },
  thGroupKh: { backgroundColor: "#3D8BD9" },
  thGroupTk: { backgroundColor: "#E66A2C" },
  thGroupVktk: { backgroundColor: "#2E7D4F" },
  thSb: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: BRAND.ink,
    backgroundColor: BRAND.bg,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "8px",
    paddingRight: "8px",
    textAlign: "center",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderLeft("1px", "dashed", BRAND.borderDashed),
    minWidth: "84px",
    whiteSpace: "nowrap",
  },
  thSbKh: {
    minWidth: "220px",
  },
  thStub: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    color: BRAND.muted,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    textAlign: "left",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: BRAND.bg,
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    minWidth: "260px",
    position: "sticky",
    left: 0,
    zIndex: 1,
  },
  tdKlasse: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
    backgroundColor: BRAND.white,
    minWidth: "260px",
    position: "sticky",
    left: 0,
    zIndex: 1,
    fontWeight: 600,
  },
  tdCell: {
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "6px",
    paddingRight: "6px",
    textAlign: "right",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.ink,
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
    ...shorthands.borderLeft("1px", "dashed", BRAND.borderDashed),
    whiteSpace: "nowrap",
  },
  tdCellEmpty: {
    color: BRAND.muted,
    fontStyle: "italic",
    textAlign: "center",
  },
});

const formatEur = (n: number): string =>
  `${n.toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;

const VereinbarungTable = ({ vereinbarung }: { vereinbarung: Vereinbarung }) => {
  const styles = useStyles();
  const groupCounts = {
    kh: SAMMELGRID_COLUMNS.filter((c) => c.group === "kh").length,
    tk: SAMMELGRID_COLUMNS.filter((c) => c.group === "tk").length,
    vktk: SAMMELGRID_COLUMNS.filter((c) => c.group === "vktk").length,
  };

  return (
    <div className={styles.tableScroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.thStub} rowSpan={2}>
              Klasse
            </th>
            <th
              className={mergeClasses(styles.thGroup, styles.thGroupKh)}
              colSpan={groupCounts.kh}
              title={GROUP_LONG.kh}
            >
              <span className={styles.thGroupShort}>{GROUP_LABEL.kh}</span>
              <span className={styles.thGroupLong}>{GROUP_LONG.kh}</span>
            </th>
            <th
              className={mergeClasses(styles.thGroup, styles.thGroupTk)}
              colSpan={groupCounts.tk}
              title={GROUP_LONG.tk}
            >
              <span className={styles.thGroupShort}>{GROUP_LABEL.tk}</span>
              <span className={styles.thGroupLong}>{GROUP_LONG.tk}</span>
            </th>
            <th
              className={mergeClasses(styles.thGroup, styles.thGroupVktk)}
              colSpan={groupCounts.vktk}
              title={GROUP_LONG.vktk}
            >
              <span className={styles.thGroupShort}>{GROUP_LABEL.vktk}</span>
              <span className={styles.thGroupLong}>{GROUP_LONG.vktk}</span>
            </th>
          </tr>
          <tr>
            {SAMMELGRID_COLUMNS.map((col) => (
              <th
                key={col.key}
                className={mergeClasses(styles.thSb, (col.group === "kh") && styles.thSbKh)}
                title={GROUP_LONG[col.group]}
              >
                {col.sb ?? GROUP_LABEL[col.group]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {vereinbarung.rows.map((row) => (
            <tr key={row.klasse}>
              <td className={styles.tdKlasse}>{KLASSEN_LABELS[row.klasse]}</td>
              {SAMMELGRID_COLUMNS.map((col) => {
                const v = row.cells[col.key];
                const empty = v === undefined || v === null;
                return (
                  <td
                    key={col.key}
                    className={mergeClasses(styles.tdCell, empty && styles.tdCellEmpty)}
                  >
                    {empty ? "—" : formatEur(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default VereinbarungTable;
